import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import { Link, useParams } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useSurveyCreateAndEdit } from "./useSurveyCreateAndEdit";
import { TableQuestions } from "./components/TableQuestions";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { EntitySelector } from "modals/EntitySelector";
import { Survey } from "types/survey";
import * as Yup from "yup";

const validationSchema = Yup.object({
  name: Yup.string().required("Survey name is required"),
});

const initialValues = {
  name: "",
};

type SurveyCreateAndEditProps = {
  edit?: boolean;
};

export const SurveyCreateAndEdit: FC<SurveyCreateAndEditProps> = ({
  edit = false,
}) => {
  const [openImportSurveyModal, setOpenImportSurveyModal] = useState(false);
  const {
    questions,
    setQuestions,
    isOrgView,
    handleCreateSurvey,
    handleEditSurvey,
    dataSurvey,
    organizationName,
    globalSurveys,
  } = useSurveyCreateAndEdit(edit);

  const titleSuffix =
    isOrgView && organizationName ? ` [${organizationName}]` : "";

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (edit) {
        handleEditSurvey(values);
      } else {
        handleCreateSurvey(values);
      }
    },
  });

  // Fill Formik values with survey data to Edit
  useEffect(() => {
    if (dataSurvey?.survey_by_pk && edit) {
      formik.setValues({
        name: dataSurvey.survey_by_pk.name || "",
      });
    }
  }, [dataSurvey, edit]);

  const { organizationId } = useParams();
  const cancelPath = organizationId
    ? `/organization/${organizationId}/surveys`
    : "/surveys";

  return (
    <MainLayoutContainer
      title={`${edit ? "Edit" : "Create"} Survey${titleSuffix}`}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_SURVEY_CREATE_AND_EDIT
          : BREADCRUMB_PAGE.SURVEY_CREATE_AND_EDIT
      }
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box>
            {!edit && (
              <Box mb={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenImportSurveyModal(true)}
                >
                  Import Survey
                </Button>
              </Box>
            )}

            <Box my={2}>
              <Typography variant="h5">Survey Information</Typography>
              <Divider />
            </Box>

            <Box mt={2}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Survey Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Box>

            <Box my={2}>
              <Typography variant="h5">Questions</Typography>
              <Divider />
            </Box>

            <Box mt={2}>
              <TableQuestions
                questions={questions}
                setQuestions={setQuestions}
              />
            </Box>

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button variant="contained" type="submit">
                  Save Changes
                </Button>
                <Button variant="outlined" component={Link} to={cancelPath}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Box>
        </form>
      </FormikProvider>

      {openImportSurveyModal && (
        <EntitySelector
          open={openImportSurveyModal}
          onClose={() => setOpenImportSurveyModal(false)}
          title="Import Survey"
          listEntities={globalSurveys}
          filterCriteria={(search: string, data: Survey) =>
            data.name.toLowerCase().includes(search.toLowerCase())
          }
          onSelect={(selected: Survey | null) => {
            if (!selected) return;

            const importedName =
              organizationName && isOrgView
                ? `${selected.name} [${organizationName}]`
                : selected.name || "";

            formik.setValues({
              name: importedName,
            });

            // Import questions and options
            const importedQuestions = (selected.questions || []).map((sq: any) => ({
              question_id: undefined, // New question, no ID yet
              title: sq.question?.title || "",
              type: sq.question?.type || "open_ended",
              body: sq.question?.body || "",
              order: sq.question?.order || 0,
              options: (sq.question?.options || []).map((opt: any) => ({
                option_id: undefined, // New option, no ID yet
                text: opt.text || "",
                order: opt.order || 0,
                question_id: "", // Will be set when question is created
              })),
            }));

            setQuestions(importedQuestions);
            setOpenImportSurveyModal(false);
          }}
          keyField="survey_id"
          displayField="name"
        />
      )}
    </MainLayoutContainer>
  );
};
